import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist/webpack.mjs';

import * as Icons from '../../assets/svg/icons';
import * as Common from '../../components/common';
import * as Hooks from '../../hooks';
import * as routeNames from '../../routes/routeName';
import { LoadingView } from '../../components/common/loader';
import { getDefaultPharmacist } from '../../utils/helper/helper';
import { useAuthSelector } from '../../services/selectors/authSelector';
import { fetchAuthorizationLetter } from '../../services/apis/driver/getAuthorizationLetter';

const buildPdfPreviewData = (pdfPayload) => {
	if (!pdfPayload || typeof pdfPayload !== 'string') {
		return null;
	}

	if (/^https?:\/\//i.test(pdfPayload) || pdfPayload.startsWith('/')) {
		return {
			viewerSource: pdfPayload,
			openUrl: pdfPayload,
		};
	}

	const normalizedBase64 = pdfPayload.includes(',')
		? pdfPayload.split(',').pop()
		: pdfPayload;

	const byteCharacters = atob(normalizedBase64 || '');
	const byteNumbers = new Array(byteCharacters.length);
	for (let index = 0; index < byteCharacters.length; index += 1) {
		byteNumbers[index] = byteCharacters.charCodeAt(index);
	}

	const byteArray = new Uint8Array(byteNumbers);
	const pdfBlob = new Blob([byteArray], { type: 'application/pdf' });
	const openUrl = URL.createObjectURL(pdfBlob);

	return {
		viewerSource: byteArray,
		openUrl,
	};
};

const revokeBlobUrl = (url) => {
	if (url && url.startsWith('blob:')) {
		URL.revokeObjectURL(url);
	}
};

const getApiErrorMessage = (requestError) => {
	const responseData = requestError?.response?.data;
	const nestedErrors = responseData?.data?.error;

	if (nestedErrors && typeof nestedErrors === 'object') {
		const messages = Object.values(nestedErrors)
			.flatMap((value) => (Array.isArray(value) ? value : [value]))
			.map((value) => String(value || '').trim())
			.filter(Boolean);

		if (messages.length > 0) {
			return messages.join(' ');
		}
	}

	return responseData?.message || requestError?.message || 'Unable to load authorization letter.';
};

const formatPersonName = (value) => {
	if (!value || typeof value !== 'string') {
		return '-';
	}

	return value
		.toLowerCase()
		.split(' ')
		.filter(Boolean)
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');
};

const SummaryItem = ({ label, value, valueClassName = '' }) => (
	<div className="min-w-0">
		<p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-400">{label}</p>
		<p className={`mt-1 text-[15px] font-semibold leading-5 text-slate-800 ${valueClassName}`}>{value}</p>
	</div>
);

const PdfPreview = ({ pdfSource, openUrl, fileName }) => {
	const containerRef = useRef(null);
	const canvasRef = useRef(null);
	const [numPages, setNumPages] = useState(0);
	const [pageNumber, setPageNumber] = useState(1);
	const [containerWidth, setContainerWidth] = useState(0);
	const [pdfDocument, setPdfDocument] = useState(null);
	const [previewLoading, setPreviewLoading] = useState(false);
	const [previewError, setPreviewError] = useState('');

	useEffect(() => {
		const updateWidth = () => {
			setContainerWidth(containerRef.current?.clientWidth || 0);
		};

		updateWidth();

		if (!window.ResizeObserver || !containerRef.current) {
			window.addEventListener('resize', updateWidth);
			return () => window.removeEventListener('resize', updateWidth);
		}

		const observer = new ResizeObserver(updateWidth);
		observer.observe(containerRef.current);

		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		setPageNumber(1);
		setNumPages(0);
		setPdfDocument(null);
		setPreviewLoading(false);
		setPreviewError('');

		if (!pdfSource) {
			return undefined;
		}

		let cancelled = false;

		const loadPdf = async () => {
			try {
				setPreviewLoading(true);
				const documentSource = typeof pdfSource === 'string'
					? pdfSource
					: { data: pdfSource.slice ? pdfSource.slice(0) : pdfSource };

				const loadingTask = pdfjsLib.getDocument(documentSource);
				const loadedPdf = await loadingTask.promise;

				if (cancelled) {
					return;
				}

				setPdfDocument(loadedPdf);
				setNumPages(loadedPdf.numPages || 0);
				setPageNumber(1);
				setPreviewError('');
			} catch (loadError) {
				if (!cancelled) {
					setPreviewError(loadError?.message || 'Unable to load PDF preview.');
				}
			} finally {
				if (!cancelled) {
					setPreviewLoading(false);
				}
			}
		};

		loadPdf();

		return () => {
			cancelled = true;
		};
	}, [pdfSource]);

	const pageWidth = Math.max(280, Math.min((containerWidth || 360) - 24, 760));

	useEffect(() => {
		if (!pdfDocument || !canvasRef.current || !pageWidth) {
			return undefined;
		}

		let cancelled = false;
		let renderTask = null;

		const renderPage = async () => {
			try {
				setPreviewLoading(true);
				setPreviewError('');

				const page = await pdfDocument.getPage(pageNumber);
				if (cancelled) {
					return;
				}

				const baseViewport = page.getViewport({ scale: 1 });
				const scale = pageWidth / baseViewport.width;
				const viewport = page.getViewport({ scale });
				const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
				const canvas = canvasRef.current;
				const context = canvas.getContext('2d');

				canvas.width = Math.floor(viewport.width * pixelRatio);
				canvas.height = Math.floor(viewport.height * pixelRatio);
				canvas.style.width = `${Math.floor(viewport.width)}px`;
				canvas.style.height = `${Math.floor(viewport.height)}px`;

				context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
				context.clearRect(0, 0, viewport.width, viewport.height);

				renderTask = page.render({
					canvasContext: context,
					viewport,
				});

				await renderTask.promise;
			} catch (renderError) {
				if (!cancelled && renderError?.name !== 'RenderingCancelledException') {
					setPreviewError(renderError?.message || 'Unable to render PDF preview.');
				}
			} finally {
				if (!cancelled) {
					setPreviewLoading(false);
				}
			}
		};

		renderPage();

		return () => {
			cancelled = true;
			if (renderTask) {
				renderTask.cancel();
			}
		};
	}, [pageNumber, pageWidth, pdfDocument]);

	const downloadPdf = () => {
		if (!openUrl) {
			return;
		}

		const link = document.createElement('a');
		link.href = openUrl;
		link.download = fileName || 'authorization-letter.pdf';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	return (
		<div className="overflow-hidden rounded-[12px] border border-slate-200 bg-slate-50">
			<div className="flex items-center justify-between gap-2 border-b border-slate-200 bg-white px-3 py-2">
				<div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
					<button
						type="button"
						onClick={() => setPageNumber((current) => Math.max(current - 1, 1))}
						disabled={pageNumber <= 1}
						className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-base disabled:cursor-not-allowed disabled:opacity-40"
						aria-label="Previous page"
					>
						&lt;
					</button>
					<span className="min-w-[58px] text-center">
						{numPages ? `${pageNumber} / ${numPages}` : '1 / -'}
					</span>
					<button
						type="button"
						onClick={() => setPageNumber((current) => Math.min(current + 1, numPages || current))}
						disabled={!numPages || pageNumber >= numPages}
						className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-base disabled:cursor-not-allowed disabled:opacity-40"
						aria-label="Next page"
					>
						&gt;
					</button>
				</div>

				<button
					type="button"
					onClick={downloadPdf}
					disabled={!openUrl}
					className="rounded-[8px] bg-[#3b82f6] px-3 py-2 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
				>
					Download
				</button>
			</div>

			<div ref={containerRef} className="h-[68vh] overflow-auto px-2 py-4">
				{previewLoading && !pdfDocument ? (
					<div className="flex min-h-[40vh] items-center justify-center text-sm font-medium text-slate-500">
						Loading PDF...
					</div>
				) : previewError ? (
					<div className="rounded-[12px] border border-dashed border-red-200 bg-red-50 px-4 py-10 text-center text-sm font-medium text-red-700">
						Unable to preview this PDF. Please download it to view the file.
						<span className="mt-2 block break-words text-[11px] font-normal text-red-500">
							{previewError}
						</span>
					</div>
				) : pdfDocument ? (
					<div className="flex justify-center">
						<canvas ref={canvasRef} className="bg-white shadow-sm" />
					</div>
				) : (
					<div className="rounded-[12px] border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
						PDF preview is not available.
					</div>
				)}

				{previewLoading && pdfDocument ? (
					<div className="pointer-events-none sticky bottom-3 mt-3 flex justify-center">
						<div className="rounded-full bg-white/90 px-3 py-2 text-xs font-semibold text-slate-500 shadow-sm">
							Loading PDF...
						</div>
					</div>
				) : null}
			</div>
		</div>
	);
};

const DriverAuthorizationLetter = () => {
	const navigate = Hooks.useCustomNavigate();
	const { pharmacyId, driverDefaultPharmacist } = useAuthSelector();
	const defaultPharmacist = useMemo(
		() => getDefaultPharmacist(pharmacyId, driverDefaultPharmacist),
		[driverDefaultPharmacist, pharmacyId]
	);
	const pharmacistUser = defaultPharmacist?.[0]?.driver_pharmacist_user;
	const isPharmacistForAuthorizationLetter = pharmacistUser?.type === 'pharmacist';
	const [loading, setLoading] = useState(true);
	const [letterData, setLetterData] = useState(null);
	const [pdfPreview, setPdfPreview] = useState(null);
	const [error, setError] = useState('');
	const [emptyMessage, setEmptyMessage] = useState('');

	useEffect(() => {
		if (isPharmacistForAuthorizationLetter) {
			navigate(routeNames.DASHBOARD_PHARMACY);
			return undefined;
		}

		let cancelled = false;
		let resolvedPdfPreview = null;

		if (!pharmacyId) {
			setPdfPreview((currentPreview) => {
				revokeBlobUrl(currentPreview?.openUrl);
				return null;
			});
			setLoading(false);
			setEmptyMessage('Select a pharmacy to view your Authorization Letter.');
			return undefined;
		}

		setLoading(true);
		setError('');
		setEmptyMessage('');
		setLetterData(null);
		setPdfPreview((currentPreview) => {
			revokeBlobUrl(currentPreview?.openUrl);
			return null;
		});

		fetchAuthorizationLetter(pharmacyId)
			.then((response) => {
				if (cancelled) {
					return;
				}

				const data = response?.data;
				resolvedPdfPreview = buildPdfPreviewData(
					data?.pdf_base64 || data?.pdf || data?.pdf_url || data?.url
				);

				setLetterData(data || null);
				setPdfPreview(resolvedPdfPreview);
			})
			.catch((requestError) => {
				if (cancelled) {
					return;
				}

				const message = getApiErrorMessage(requestError);
				if (requestError?.response?.status === 404) {
					setEmptyMessage(message);
					return;
				}

				setError(message);
			})
			.finally(() => {
				if (!cancelled) {
					setLoading(false);
				}
			});

		return () => {
			cancelled = true;
			revokeBlobUrl(resolvedPdfPreview?.openUrl);
		};
	}, [isPharmacistForAuthorizationLetter, navigate, pharmacyId]);

	const showContent = !loading && !error && !emptyMessage && letterData;
	const driverName = useMemo(() => formatPersonName(letterData?.driver_name), [letterData?.driver_name]);
	const pharmacistName = useMemo(
		() => formatPersonName(letterData?.pharmacist_name),
		[letterData?.pharmacist_name]
	);
	const startDateLabel = useMemo(
		() => letterData?.start_date_format || letterData?.start_date || '-',
		[letterData?.start_date, letterData?.start_date_format]
	);
	const endDateLabel = useMemo(() => {
		if (letterData?.end_date_format) {
			return letterData.end_date_format;
		}

		if (letterData?.end_date) {
			return letterData.end_date;
		}

		return 'Until Revoked';
	}, [letterData?.end_date, letterData?.end_date_format]);
	const pdfFileName = letterData?.file_name || 'authorization-letter.pdf';

	if (isPharmacistForAuthorizationLetter) {
		return null;
	}

	return (
		<div className="app-wrapper flex justify-center">
			<div className="app-screen no-shades w-full xl:w-3/12 lg:w-4/12 md:w-5/12 sm:w-6/12 overflow-x-hidden">
				<div className="relative p-4 pb-24">
					<Common.TopBar />
					{loading ? (
						<div className="flex min-h-[55vh] items-center justify-center">
							<LoadingView />
						</div>
					) : (
						<>
							<div className="mt-2 flex items-center gap-3">
								<button type="button" onClick={() => navigate(routeNames.DASHBOARD_PHARMACY)}>
									<Icons.HomeIcon />
								</button>
								<div className="text-info flex-1">
									<h1 className="text-lg font-bold">Authorization Letter</h1>
								</div>
							</div>

							{showContent ? (
								<div className="mt-3 rounded-[12px] border border-slate-200 bg-slate-50 px-4 py-3">
									<div className="grid grid-cols-2 gap-x-5 gap-y-3">
										<SummaryItem label="Driver" value={driverName} />
										<SummaryItem label="Pharmacist" value={pharmacistName} />
										<SummaryItem label="Start Date" value={startDateLabel} />
										<SummaryItem
											label="End Date"
											value={endDateLabel}
											valueClassName={letterData?.is_expired ? 'text-red-700' : ''}
										/>
									</div>
								</div>
							) : null}

							{error ? (
								<div className="mt-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-4 text-sm font-medium text-red-700">
									{error}
								</div>
							) : null}

							{emptyMessage ? (
								<div className="mt-6 rounded-2xl border border-slate-200 bg-white px-4 py-6 text-center text-sm font-medium text-slate-600">
									{emptyMessage}
								</div>
							) : null}

							{showContent ? (
								<div className="mt-5 rounded-[16px] border border-slate-200 bg-white p-3">
									<p className="mb-2 px-1 text-sm font-semibold text-slate-800">Authorization Letter</p>
									{pdfPreview?.viewerSource ? (
										<PdfPreview
											pdfSource={pdfPreview.viewerSource}
											openUrl={pdfPreview.openUrl}
											fileName={pdfFileName}
										/>
									) : (
										<div className="rounded-[12px] border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
											PDF preview is not available.
										</div>
									)}
								</div>
							) : null}
						</>
					)}
				</div>

				{!loading ? <Common.BottomBar pharmacyId={pharmacyId} /> : null}
			</div>
		</div>
	);
};

export default DriverAuthorizationLetter;
